import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit2, Trash2, Award, User, Calendar } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyEntry = {
  player_name: "",
  player_photo: "",
  tournament_name: "",
  date: "",
  prize: "",
};

export default function HallOfFameManager() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState(emptyEntry);
  const { getAuthHeaders } = useAuth();

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/hall-of-fame`);
      setEntries(response.data);
    } catch (err) {
      logger.error("Error fetching hall of fame:", err);
      toast.error("Failed to load hall of fame");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const openCreateDialog = () => {
    setEditingEntry(null);
    setFormData(emptyEntry);
    setDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      player_name: entry.player_name,
      player_photo: entry.player_photo || "",
      tournament_name: entry.tournament_name,
      date: entry.date,
      prize: entry.prize || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.player_name || !formData.tournament_name || !formData.date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      if (editingEntry) {
        await axios.put(`${API}/hall-of-fame/${editingEntry.id}`, formData, getAuthHeaders());
        toast.success("Winner updated!");
      } else {
        await axios.post(`${API}/hall-of-fame`, formData, getAuthHeaders());
        toast.success("Winner added!");
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      toast.error("Failed to save entry");
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this winner?")) return;

    try {
      await axios.delete(`${API}/hall-of-fame/${entryId}`, getAuthHeaders());
      toast.success("Winner removed!");
      fetchEntries();
    } catch (err) {
      toast.error("Failed to delete entry");
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading hall of fame...</div>;
  }

  return (
    <div data-testid="hall-of-fame-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2">Hall of Fame Manager</h1>
          <p className="text-white/60">Celebrate your tournament champions</p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-gold text-black hover:bg-gold-dark"
          data-testid="add-winner-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Winner
        </Button>
      </div>

      {/* Winners Grid */}
      {entries.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Award className="w-12 h-12 text-gold/30 mx-auto mb-4" />
          <p className="text-white/60">No winners yet. Add your first champion!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="glass-card overflow-hidden"
              data-testid={`winner-card-${entry.id}`}
            >
              <div className="aspect-video bg-gradient-to-br from-gold/20 to-casino-dark flex items-center justify-center">
                {entry.player_photo ? (
                  <img
                    src={entry.player_photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${entry.player_photo}` : entry.player_photo}
                    alt={entry.player_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-16 h-16 text-gold/30" />
                )}
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl text-white mb-1">{entry.player_name}</h3>
                <p className="text-gold text-sm mb-2">{entry.tournament_name}</p>
                <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
                  <Calendar className="w-4 h-4" />
                  <span>{entry.date}</span>
                </div>
                {entry.prize && (
                  <p className="text-gold font-medium text-sm">{entry.prize}</p>
                )}
                <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(entry)}
                    className="flex-1 border-white/20 text-white hover:border-gold hover:text-gold"
                    data-testid={`edit-winner-${entry.id}`}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(entry.id)}
                    className="border-white/20 text-white hover:border-red-500 hover:text-red-500"
                    data-testid={`delete-winner-${entry.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-casino-dark border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingEntry ? "Edit Winner" : "Add Winner"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="hall-of-fame-form">
            <div>
              <Label className="text-white/80">Player Name *</Label>
              <Input
                value={formData.player_name}
                onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="Enter player name"
                data-testid="winner-name-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Tournament Name *</Label>
              <Input
                value={formData.tournament_name}
                onChange={(e) => setFormData({ ...formData, tournament_name: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., Saturday $1K Guarantee"
                data-testid="winner-tournament-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Date *</Label>
              <Input
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., January 15, 2025"
                data-testid="winner-date-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Prize (optional)</Label>
              <Input
                value={formData.prize}
                onChange={(e) => setFormData({ ...formData, prize: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="e.g., $1,500"
                data-testid="winner-prize-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Photo URL (optional)</Label>
              <Input
                value={formData.player_photo}
                onChange={(e) => setFormData({ ...formData, player_photo: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="https://... or /api/uploads/..."
                data-testid="winner-photo-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gold text-black hover:bg-gold-dark"
                data-testid="winner-submit-btn"
              >
                {editingEntry ? "Update Winner" : "Add Winner"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
